import type { MeResponse, TeamSummary } from '@greenroom/shared'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useSyncExternalStore, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router'
import { FullPageSpinner } from '../components/ui/spinner'
import { api } from './api'
import { store } from './storage'

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: () => api<MeResponse | null>('/api/auth/me'),
  staleTime: 60_000,
})

export function useMe() {
  return useQuery(meQuery)
}

/** The signed-in user, for components that only render behind `RequireAuth`. */
export function useUser(): MeResponse {
  const { data } = useMe()
  if (!data) throw new Error('useUser outside of RequireAuth')
  return data
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { data, isPending } = useMe()
  const location = useLocation()
  if (isPending) return <FullPageSpinner />
  if (!data) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  return children
}

/** Only follows app-internal paths, so a crafted link cannot bounce people to another site. */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/studios'
}

// ---------- current team ----------

const TEAM_KEY = 'greenroom.team'
const listeners = new Set<() => void>()
const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function selectTeam(id: string) {
  store.set(TEAM_KEY, id)
  listeners.forEach((l) => l())
}

/** Only for pages inside the shell, which handles the no-team case before rendering them. */
export function useCurrentTeam(): { team: TeamSummary; teams: TeamSummary[]; setTeam: (id: string) => void } {
  const { teams } = useUser()
  const stored = useSyncExternalStore(subscribe, () => store.get<string | null>(TEAM_KEY, null))
  const team = teams.find((t) => t.id === stored) ?? teams[0]
  if (!team) throw new Error('useCurrentTeam without a team')
  return { team, teams, setTeam: selectTeam }
}
